import type { UiStrings } from '@/constants/ui-locale'
import { formatLanguageCode } from '@/constants/languages'

export type SharedRoomMember = {
  name: string
  joinedAt?: number
  role?: 'host' | 'you'
}

export type SharedRoomProjectReason =
  | 'majority'
  | 'title'
  | 'access'
  | 'first_joined'
  | 'manual'

export type SharedRoomProject = {
  key: string
  reason: SharedRoomProjectReason
  changedBy?: string
}

const SHARED_ROOM_PROJECT_REASONS: readonly SharedRoomProjectReason[] = [
  'majority',
  'title',
  'access',
  'first_joined',
  'manual',
]

function parseRoomProject(raw: unknown): SharedRoomProject | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const row = raw as Record<string, unknown>
  if (typeof row.key !== 'string' || !row.key.trim()) return undefined
  const reason = row.reason
  if (
    typeof reason !== 'string' ||
    !SHARED_ROOM_PROJECT_REASONS.includes(reason as SharedRoomProjectReason)
  ) {
    return undefined
  }
  const project: SharedRoomProject = {
    key: row.key.trim(),
    reason: reason as SharedRoomProjectReason,
  }
  if (
    project.reason === 'manual' &&
    typeof row.changedBy === 'string' &&
    row.changedBy.trim()
  ) {
    project.changedBy = row.changedBy.trim()
  }
  return project
}

function readRoomProject(record: Record<string, unknown>): SharedRoomProject | undefined {
  const nested =
    typeof record.meta === 'object' && record.meta !== null
      ? (record.meta as Record<string, unknown>)
      : null
  for (const source of [record, nested]) {
    if (!source) continue
    const parsed = parseRoomProject(source.roomProject)
    if (parsed) return parsed
  }
  return undefined
}

/** Optional fields the AI proxy may send on shared-room WebSocket frames. */
export type SharedRoomMatchMeta = {
  peerMeetingTitle?: string
  roomProject?: SharedRoomProject
  startedAt?: string | number
  sourceLang?: string
  targetLang?: string
  hostName?: string
  audioSourceLabel?: string
  members?: SharedRoomMember[]
}

export type SharedRoomMatchDetails = SharedRoomMatchMeta & {
  matchId: string
  peerNames: string[]
}

function readPeerMeetingTitle(record: Record<string, unknown>): string | undefined {
  const nested =
    typeof record.meta === 'object' && record.meta !== null
      ? (record.meta as Record<string, unknown>)
      : null
  for (const source of [record, nested]) {
    if (!source) continue
    if (typeof source.peerMeetingTitle === 'string') return source.peerMeetingTitle
    if (typeof source.peer_meeting_title === 'string') return source.peer_meeting_title
    /** BrSE has shipped `meetingTitle` on shared-room frames as the peer's title. */
    if (typeof source.meetingTitle === 'string') return source.meetingTitle
  }
  return undefined
}

export function parseSharedRoomMatchMeta(record: Record<string, unknown>): SharedRoomMatchMeta {
  const meta: SharedRoomMatchMeta = {}
  const peerTitle = readPeerMeetingTitle(record)
  if (peerTitle) meta.peerMeetingTitle = peerTitle
  if (typeof record.hostName === 'string') meta.hostName = record.hostName
  if (typeof record.audioSourceLabel === 'string') meta.audioSourceLabel = record.audioSourceLabel
  if (typeof record.sourceLang === 'string') meta.sourceLang = record.sourceLang
  if (typeof record.targetLang === 'string') meta.targetLang = record.targetLang
  if (typeof record.startedAt === 'string' || typeof record.startedAt === 'number') {
    meta.startedAt = record.startedAt
  }
  const roomProject = readRoomProject(record)
  if (roomProject) meta.roomProject = roomProject
  if (Array.isArray(record.members)) {
    meta.members = record.members
      .map((entry): SharedRoomMember | null => {
        if (typeof entry !== 'object' || entry === null) return null
        const row = entry as Record<string, unknown>
        if (typeof row.name !== 'string') return null
        const member: SharedRoomMember = { name: row.name }
        if (typeof row.joinedAt === 'number') member.joinedAt = row.joinedAt
        if (row.role === 'host' || row.role === 'you') member.role = row.role
        return member
      })
      .filter((m): m is SharedRoomMember => m !== null)
  }
  return meta
}

function formatStartedAt(startedAt: string | number | undefined, locale: string): string | null {
  if (startedAt === undefined) return null
  const date =
    typeof startedAt === 'number'
      ? new Date(startedAt)
      : Number.isFinite(Number(startedAt))
        ? new Date(Number(startedAt))
        : new Date(startedAt)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
}

/** Others in the room (excludes `role: 'you'`), aligned with roster presentation. */
export function countSharedRoomOthers(
  details: Pick<SharedRoomMatchDetails, 'peerNames' | 'members'>
): number {
  if (details.members?.length) {
    return details.members.filter((member) => member.role !== 'you').length
  }
  return details.peerNames.length
}

export function buildSharedMeetingPresentation(input: {
  details: Pick<SharedRoomMatchDetails, 'peerNames' | 'peerMeetingTitle' | 'startedAt' | 'sourceLang' | 'targetLang' | 'hostName' | 'audioSourceLabel' | 'members'>
  roomId?: string | null
  ownMeetingTitle?: string
  ownUserName?: string
  meetingCreatedAt?: string
  meetingSourceLang?: string
  meetingTargetLang?: string
  t: UiStrings
  locale: string
}): {
  meetingTitle: string | null
  startedLabel: string | null
  languageLabel: string
  hostName: string | null
  audioSourceLabel: string | null
  participants: SharedRoomMember[]
} {
  const {
    details,
    ownUserName,
    meetingCreatedAt,
    meetingSourceLang,
    meetingTargetLang,
    t,
    locale,
  } = input

  const peerTitle = details.peerMeetingTitle?.trim()
  const meetingTitle = peerTitle ? peerTitle : null

  const startedLabel =
    formatStartedAt(details.startedAt, locale) ??
    (meetingCreatedAt ? formatStartedAt(meetingCreatedAt, locale) : null)

  const source = details.sourceLang ?? meetingSourceLang
  const target = details.targetLang ?? meetingTargetLang
  const sourceLabel = source ? formatLanguageCode(source) : t.autoDetectBadge
  const targetLabel = target ? formatLanguageCode(target) : '—'
  const languageLabel = `${sourceLabel} → ${targetLabel}`

  let participants: SharedRoomMember[]
  if (details.members?.length) {
    participants = [...details.members]
  } else if (details.peerNames.length > 0) {
    participants = details.peerNames.map((name) => ({ name }))
    if (ownUserName && !participants.some((p) => p.name === ownUserName)) {
      participants.push({ name: ownUserName, role: 'you' })
    }
  } else if (ownUserName) {
    participants = [{ name: ownUserName, role: 'you' }]
  } else {
    participants = []
  }

  return {
    meetingTitle,
    startedLabel,
    languageLabel,
    hostName: details.hostName ?? null,
    audioSourceLabel: details.audioSourceLabel ?? null,
    participants,
  }
}
